import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import React, { useState } from "react";
import styled from "styled-components";
import { body1, heading5 } from "~/guidelines/Typography";

interface Tab {
  key: string;
  label: string;
  icon: any;
  text: string;
}

interface Props {
  tabs: Tab[];
}

const TabsContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.darkFirst};
  color: ${({ theme }) => theme.color.text.darkFirst};
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  max-width: 1200px;
  margin: 0 auto;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  ${heading5}
  background-color: ${({ $isActive }) =>
    $isActive ? "transparent" : "#413D3A"};
  color: ${({ theme }) => theme.color.text.darkFirst};
  border: none;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 230px;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.color.background.darkFirst};
  }
`;

const UpperIcon = styled.div`
  width: 49px;
  height: 23px;
  background-color: ${({ theme }) => theme.color.background.lightThird};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border-top: 2px solid black;
  border-left: 2px solid black;
  border-right: 2px solid black;
`;

const IconContainer = styled.div`
  box-sizing: border-box;
  width: 49px;
  height: 49px;
  background-color: white;
  position: relative;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  border: 2px solid black;

  > img,
  > svg {
    align-self: center;
    width: 25px;
    height: 25px;
    margin-bottom: 13px;
    z-index: 1;
  }
`;

const TabContent = styled.div`
  ${body1}
  display: flex;
  padding: 80px 120px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.color.background.darkFirst};
  color: ${({ theme }) => theme.color.text.darkFirst};
`;

const DesktopInvestorTypeTabs: React.FC<Props> = ({ tabs }) => {
  const [selected, setSelected] = useState("fund");
  const currentTab = tabs.find((tab) => tab.key === selected);

  return (
    <>
      <TabsContainer>
        {tabs.map((tab) => (
          <TabButton
            key={tab.key}
            $isActive={selected === tab.key}
            onClick={() => setSelected(tab.key)}
          >
            <UpperIcon />

            <IconContainer>
              <Image src={tab.icon} alt={tab.label} />
            </IconContainer>

            {tab.label}
          </TabButton>
        ))}
      </TabsContainer>

      <TabContent>
        <AnimatePresence mode="wait">
          <motion.span
            key={currentTab?.text}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ display: "inline-block" }}
          >
            {currentTab?.text}
          </motion.span>
        </AnimatePresence>
      </TabContent>
    </>
  );
};

export default DesktopInvestorTypeTabs;
